import type { ReactNode } from 'react';
import { Modal } from '@adiox/ui';
import { describeSessions } from './columns';
import type { SymbolInfo } from './useSymbols';

const CALC_MODES: Record<string, string> = {
  Forex: 'Forex',
  ForexNoLeverage: 'Forex, no leverage',
  Cfd: 'CFD',
  CfdLeverage: 'CFD with leverage',
  CfdIndex: 'CFD index',
  Futures: 'Futures',
};

const TRADE_MODES: Record<string, string> = {
  Full: 'Buy and sell',
  LongOnly: 'Buy only',
  ShortOnly: 'Sell only',
  CloseOnly: 'Close only',
  Disabled: 'Trading disabled',
};

const SWAP_MODES: Record<string, string> = {
  Disabled: 'No swaps',
  Points: 'In points',
  CurrencySymbol: 'In the base currency',
  CurrencyMargin: 'In the margin currency',
  CurrencyDeposit: 'In the account currency',
  InterestCurrent: 'Interest, % a year (current price)',
  InterestOpen: 'Interest, % a year (open price)',
};

// Fields a server older than the specification window does not send show as a dash rather than failing.
const trim = (v: number | undefined | null) => (typeof v === 'number' ? String(Number(v.toFixed(8))) : '—');

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="flex flex-col gap-1">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">{title}</h3>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-sm">{children}</dl>
    </section>
  );
}

function Item({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="num text-right">{children}</dd>
    </>
  );
}

/** A symbol's specification as the user's group trades it, like MT5's Specification window. */
export function SymbolSpecDialog({
  info,
  leverage,
  currency,
  onClose,
}: {
  info: SymbolInfo | null;
  leverage: number;
  currency: string;
  onClose: () => void;
}) {
  return (
    <Modal open={info !== null} title={info ? `${info.name} specification` : ''} onClose={onClose} size="md">
      {info && (
        <div className="flex max-h-[70vh] flex-col gap-4 overflow-auto pr-1" data-testid="symbol-spec">
          {info.description && <p className="text-sm text-muted">{info.description}</p>}
          <Section title="Contract">
            <Item label="Digits">{info.digits}</Item>
            <Item label="Contract size">
              {trim(info.contractSize)} {info.currencyBase}
            </Item>
            <Item label="Tick size">{trim(info.tickSize)}</Item>
            <Item label="Tick value">{trim(info.tickValue)}</Item>
            <Item label="Profit currency">{info.currencyProfit}</Item>
            <Item label="Stops level">{info.stopsLevel} points</Item>
            <Item label="Freeze level">{info.freezeLevel} points</Item>
            <Item label="Trading">{TRADE_MODES[info.tradeMode] ?? info.tradeMode}</Item>
          </Section>
          <Section title="Volume">
            <Item label="Minimum">{trim(info.volumeMin)} lots</Item>
            <Item label="Maximum">{trim(info.volumeMax)} lots</Item>
            <Item label="Step">{trim(info.volumeStep)} lots</Item>
          </Section>
          <Section title="Margin">
            <Item label="Calculation">{CALC_MODES[info.calcMode] ?? info.calcMode}</Item>
            <Item label="Margin currency">{info.currencyMargin}</Item>
            <Item label="Initial margin">
              {(info.marginInitial ?? 0) > 0 ? trim(info.marginInitial) : 'From the contract size'}
            </Item>
            <Item label="Hedged margin">
              {(info.marginHedged ?? 0) > 0 ? trim(info.marginHedged) : 'From the contract size'}
            </Item>
            <Item label="Margin rate">× {trim(info.marginRate)}</Item>
            {leverage > 0 && <Item label="Account leverage">1:{leverage}</Item>}
          </Section>
          <Section title="Swaps and commission">
            <Item label="Swap type">{SWAP_MODES[info.swapMode] ?? info.swapMode}</Item>
            <Item label="Swap long">{trim(info.swapLong)}</Item>
            <Item label="Swap short">{trim(info.swapShort)}</Item>
            <Item label="Triple swap on">{info.swapTripleDay ?? '—'}</Item>
            <Item label="Commission">
              {info.commissionPerLot === undefined
                ? '—'
                : info.commissionPerLot === 0
                  ? 'None'
                  : `${trim(Math.abs(info.commissionPerLot))} ${currency} per lot, each side`}
            </Item>
          </Section>
          <section aria-label="Trading hours" className="flex flex-col gap-1">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">
              Trading hours (server time)
            </h3>
            <ul className="text-sm">
              {(info.sessions === undefined ? ['—'] : describeSessions(info.sessions)).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <p className="text-sm">
              Now:{' '}
              <span className={info.sessionOpen ? 'text-up' : 'text-muted'}>
                {info.sessionOpen ? 'open' : 'closed'}
              </span>
            </p>
          </section>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-line px-4 py-1.5 text-sm hover:border-accent"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
